import pluralize from 'pluralize';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { AnyPermissionGate } from '@/modules/auth/components/permissions/AnyPermissionGate';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { CandidateAssessmentSummaryBadge } from '@/modules/candidates/components/CandidateLedgerAssessment';
import { useBulkDeclineCandidateJobMutation } from '@/modules/candidates/mutations/useBulkDeclineCandidateJobMutation';
import { useBulkEnrichCandidateJobMutation } from '@/modules/candidates/mutations/useBulkEnrichCandidateJobMutation';
import { useBulkInterviewCandidateJobMutation } from '@/modules/candidates/mutations/useBulkInterviewCandidateJobMutation';
import { useBulkPrescreenCandidateJobMutation } from '@/modules/candidates/mutations/useBulkPrescreenCandidateJobMutation';
import { useBulkReachOutCandidateJobMutation } from '@/modules/candidates/mutations/useBulkReachOutCandidateJobMutation';
import { useBulkRejectCandidateJobMutation } from '@/modules/candidates/mutations/useBulkRejectCandidateJobMutation';
import { useBulkShortlistCandidateJobMutation } from '@/modules/candidates/mutations/useBulkShortlistCandidateJobMutation';
import { useCreateBulkCandidateFeedbackMutation } from '@/modules/candidates/mutations/useCreateBulkCandidateFeedbackMutation';
import type {
  CandidateJobApi,
  CandidateStatusApi,
} from '@/modules/candidates/types';
import { getCandidateDisplayScore } from '@/modules/candidates/utils/candidateAssessment';
import type { CompanyApi } from '@/modules/companies/types';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { Separator } from '@/modules/ui/components/separator/Separator';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import {
  BulkCandidateAnnotationDialog,
  type CandidateForAnnotation,
} from './BulkCandidateAnnotationDialog';
import { CandidateActionButton } from './CandidateActionButton';
import {
  CandidateCard,
  CandidateCardActions,
  CandidateCardBackground,
  CandidateCardCheckbox,
  CandidateCardContent,
  CandidateCardHeader,
  CandidateCardHiringStatus,
  CandidateCardHMFeedbackBadge,
  CandidateCardReachOutMessage,
  CandidateCardTitle,
} from './CandidateCard';
import { CandidateCardSkeleton } from './CandidateCardSkeleton';
import CandidateScore from './CandidateScore';
import { GatedReachOutButton } from './GatedReachOutButton';

type BulkAction = CandidateStatusApi | 'Enrich';

interface CandidateListProps {
  jobId: string;
  candidates: CandidateJobApi[];
  isLoading: boolean;
  showCandidateBackgroundWhenLoading?: boolean;
  hiringStages: CompanyApi['hiringStages'];
  job?: Pick<JobApi, 'submissionDecisionMode'>;
}

export const CandidateList = ({
  jobId,
  candidates,
  isLoading,
  showCandidateBackgroundWhenLoading,
  hiringStages,
  job,
}: CandidateListProps) => {
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>(
    [],
  );
  const [activeBulkAction, setActiveBulkAction] = useState<BulkAction | null>(
    null,
  );
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false);
  const [annotationCandidates, setAnnotationCandidates] = useState<
    CandidateForAnnotation[]
  >([]);

  const bulkShortlistMutation = useBulkShortlistCandidateJobMutation();
  const bulkEnrichMutation = useBulkEnrichCandidateJobMutation();
  const bulkPrescreenMutation = useBulkPrescreenCandidateJobMutation();
  const bulkRejectMutation = useBulkRejectCandidateJobMutation();
  const bulkFeedbackMutation = useCreateBulkCandidateFeedbackMutation();
  const bulkReachOutMutation = useBulkReachOutCandidateJobMutation();
  const bulkDeclineMutation = useBulkDeclineCandidateJobMutation();
  const bulkInterviewMutation = useBulkInterviewCandidateJobMutation();

  const eligibleCandidates =
    activeBulkAction === 'Enrich'
      ? candidates.filter((c) => c.isPreviewOnly)
      : activeBulkAction
        ? candidates.filter((c) =>
            c.allowedTransitions.includes(activeBulkAction),
          )
        : candidates;

  const isAllSelected =
    eligibleCandidates.length > 0 &&
    eligibleCandidates.every((c) =>
      selectedCandidateIds.includes(c.candidate.id),
    );
  const isIndeterminate = !isAllSelected && selectedCandidateIds.length > 0;
  const hasSelectedCandidates = selectedCandidateIds.length > 0;

  const handleSelectAll = (checked: boolean) => {
    setSelectedCandidateIds(
      checked ? eligibleCandidates.map((c) => c.candidate.id) : [],
    );
  };

  const handleSelectCandidate = (candidateId: string, checked: boolean) => {
    setSelectedCandidateIds((previous) =>
      checked
        ? [...previous, candidateId]
        : previous.filter((id) => id !== candidateId),
    );
  };

  const handleSetActiveBulkAction = (action: BulkAction) => {
    setActiveBulkAction(action);
    setSelectedCandidateIds([]);
  };

  const handleCancelBulkAction = () => {
    setActiveBulkAction(null);
    setSelectedCandidateIds([]);
  };

  const handleBulkShortlist = () => {
    bulkShortlistMutation.mutate(
      { jobId, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)} have been shortlisted`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been shortlisted: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleBulkEnrich = () => {
    bulkEnrichMutation.mutate(
      { jobId, candidateIds: selectedCandidateIds },
      {
        onSuccess: (data) => {
          const enrichedCount =
            data?.enrichedCount ?? selectedCandidateIds.length;
          toast.success(
            `${enrichedCount} ${pluralize('candidate', enrichedCount)} ${pluralize('has', enrichedCount)} been enriched`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been enriched: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleBulkPrescreen = () => {
    bulkPrescreenMutation.mutate(
      { jobId, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)} have been moved to prescreened`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been moved to prescreened: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const openRejectAnnotationDialog = () => {
    const selected = candidates.filter((c) =>
      selectedCandidateIds.includes(c.candidate.id),
    );
    setAnnotationCandidates(
      selected.map((c) => {
        const headline =
          c.candidate.headline ??
          (c.candidate.workExperiences?.at(0)?.jobTitle &&
          c.candidate.workExperiences?.at(0)?.companyName
            ? `${c.candidate.workExperiences[0]!.jobTitle} at ${c.candidate.workExperiences[0]!.companyName}`
            : null);
        return {
          id: c.candidate.id,
          candidateJobId: c.id,
          fullName: `${c.candidate.firstName} ${c.candidate.lastName}`,
          headline: headline ?? null,
          location: c.candidate.location ?? null,
          score: getCandidateDisplayScore(c),
          assessment: c.assessment,
          assessmentLedgerRevisionId:
            c.assessment.mode === 'ledger'
              ? c.assessment.displayedRevisionId
              : null,
          strengths: (c.strengths ?? []).map((s) => ({ summary: s.summary })),
          clarifications: (c.clarifications ?? []).map((cl) => ({
            summary: cl.summary,
          })),
        };
      }),
    );
    setAnnotationDialogOpen(true);
  };

  const handleAnnotationConfirm = (
    annotations: Record<string, string | null>,
  ) => {
    const isPositive = false;
    const actionLabel = 'rejected';

    const candidateJobIdByCandiateId = Object.fromEntries(
      annotationCandidates.map((c) => [c.id, c.candidateJobId]),
    );
    const assessmentRevisionIdByCandidateId = Object.fromEntries(
      annotationCandidates.map((candidate) => [
        candidate.id,
        candidate.assessmentLedgerRevisionId,
      ]),
    );

    const feedbackItems = Object.entries(annotations)
      .filter(([, annotation]) => annotation !== null)
      .map(([candidateId, annotation]) => ({
        candidateJobId: candidateJobIdByCandiateId[candidateId]!,
        feedback: {
          isPositive,
          annotation: annotation as string,
          assessmentLedgerRevisionId:
            assessmentRevisionIdByCandidateId[candidateId],
        },
      }));

    bulkRejectMutation.mutate(
      { jobId, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)} have been ${actionLabel}`,
          );

          if (feedbackItems.length > 0) {
            bulkFeedbackMutation.mutate({
              jobId,
              body: { items: feedbackItems },
            });
          }

          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
          setAnnotationDialogOpen(false);
          setAnnotationCandidates([]);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been ${actionLabel}: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleBulkReachOut = () => {
    bulkReachOutMutation.mutate(
      { jobId: jobId!, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `Reach out messages have been generated for ${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)}.`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Reach out messages have not been generated: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleBulkDecline = () => {
    bulkDeclineMutation.mutate(
      { jobId: jobId!, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)} have been declined`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been declined: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleBulkInterview = () => {
    bulkInterviewMutation.mutate(
      { jobId: jobId!, candidateIds: selectedCandidateIds },
      {
        onSuccess: () => {
          toast.success(
            `${selectedCandidateIds.length} ${pluralize('candidate', selectedCandidateIds.length)} have been moved to interviewing`,
          );
          setSelectedCandidateIds([]);
          setActiveBulkAction(null);
        },
        onError: (error) => {
          toast.error(
            `Candidates have not been moved to interviewing: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const confirmHandlerMap: Partial<Record<BulkAction, () => void>> = {
    Shortlisted: handleBulkShortlist,
    Enrich: handleBulkEnrich,
    Prescreened: handleBulkPrescreen,
    Rejected: openRejectAnnotationDialog,
    Declined: handleBulkDecline,
    ContactStarted: handleBulkReachOut,
    Interviewing: handleBulkInterview,
  };

  const handleConfirm = () => {
    if (activeBulkAction) confirmHandlerMap[activeBulkAction]?.();
  };

  const availableTransitions = new Set<BulkAction>(
    candidates.flatMap((c) => c.allowedTransitions),
  );
  if (candidates.some((c) => c.isPreviewOnly)) {
    availableTransitions.add('Enrich');
  }

  type BulkActionDef = {
    label: string;
    action: BulkAction;
    isPending: boolean;
    isDestructive?: boolean;
    isReachOut?: boolean;
  };

  const allBulkActionDefs: BulkActionDef[] = [
    {
      label: 'Shortlist',
      action: 'Shortlisted',
      isPending: bulkShortlistMutation.isPending,
    },
    {
      label: 'Enrich',
      action: 'Enrich',
      isPending: bulkEnrichMutation.isPending,
    },
    {
      label: 'Prescreen',
      action: 'Prescreened',
      isPending: bulkPrescreenMutation.isPending,
    },
    {
      label: 'Reach out',
      action: 'ContactStarted',
      isPending: bulkReachOutMutation.isPending,
      isReachOut: true,
    },
    {
      label: 'Interview',
      action: 'Interviewing',
      isPending: bulkInterviewMutation.isPending,
    },
    {
      label: 'Reject',
      action: 'Rejected',
      isPending: bulkRejectMutation.isPending,
      isDestructive: true,
    },
    {
      label: 'Decline',
      action: 'Declined',
      isPending: bulkDeclineMutation.isPending,
      isDestructive: true,
    },
  ];

  const bulkActionDefs = allBulkActionDefs.filter(({ action }) =>
    availableTransitions.has(action),
  );

  return (
    <>
      <BulkCandidateAnnotationDialog
        open={annotationDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAnnotationDialogOpen(false);
            setAnnotationCandidates([]);
          }
        }}
        jobId={jobId}
        isPositive={false}
        candidates={annotationCandidates}
        onConfirm={handleAnnotationConfirm}
        isLoading={
          bulkRejectMutation.isPending ||
          bulkFeedbackMutation.isPending ||
          bulkEnrichMutation.isPending
        }
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className={cn(!activeBulkAction && 'invisible')}>
            <AnyPermissionGate
              permissions={[
                permissions.CandidateJobs.Write,
                permissions.Messaging.Write,
              ]}
            >
              <label className="flex cursor-pointer items-center gap-2 pr-2">
                <Checkbox
                  checked={isIndeterminate ? 'indeterminate' : isAllSelected}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm">Select All</span>
              </label>
            </AnyPermissionGate>
          </div>

          {bulkActionDefs.map(
            ({ label, action, isPending, isDestructive, isReachOut }) => {
              const isActive = activeBulkAction === action;
              if (isReachOut) {
                return (
                  <GatedReachOutButton
                    key={action}
                    variant="outline"
                    isPending={isPending}
                    onClick={() => handleSetActiveBulkAction(action)}
                    className={cn(
                      isActive &&
                        'border-primary text-primary hover:border-primary hover:text-primary',
                    )}
                  >
                    {label}
                  </GatedReachOutButton>
                );
              }
              return (
                <PermissionsGate
                  key={action}
                  permissions={[permissions.CandidateJobs.Write]}
                >
                  <Button
                    variant="outline"
                    loading={isPending}
                    onClick={() => handleSetActiveBulkAction(action)}
                    className={cn(
                      isDestructive
                        ? isActive
                          ? 'border-destructive text-destructive hover:border-destructive hover:bg-destructive/5 hover:text-destructive'
                          : 'border-destructive/40 text-destructive hover:border-destructive/60 hover:bg-destructive/5 hover:text-destructive'
                        : isActive &&
                            'border-primary text-primary hover:border-primary hover:text-primary',
                    )}
                  >
                    {label}
                  </Button>
                </PermissionsGate>
              );
            },
          )}

          {activeBulkAction && (
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" onClick={handleCancelBulkAction}>
                Cancel
              </Button>
              {hasSelectedCandidates && (
                <Button variant="primary" onClick={handleConfirm}>
                  Confirm ({selectedCandidateIds.length})
                </Button>
              )}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 10 }).map((_, index) => (
              <CandidateCardSkeleton
                key={index}
                showCandidateBackground={showCandidateBackgroundWhenLoading}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {candidates.map((candidateJob) => {
              const isEligible =
                !activeBulkAction ||
                (activeBulkAction === 'Enrich' && candidateJob.isPreviewOnly) ||
                (activeBulkAction !== 'Enrich' &&
                  candidateJob.allowedTransitions.includes(activeBulkAction));
              const isSelected = selectedCandidateIds.includes(
                candidateJob.candidate.id,
              );
              return (
                <CandidateCard
                  key={candidateJob.id}
                  selected={isSelected}
                  ineligible={!!activeBulkAction && !isEligible}
                >
                  <CandidateCardHeader>
                    {activeBulkAction && (
                      <CandidateCardCheckbox
                        candidateId={candidateJob.candidate.id}
                        selected={isSelected}
                        onSelect={handleSelectCandidate}
                        disabled={!isEligible}
                      />
                    )}
                    <CandidateCardTitle
                      candidate={candidateJob.candidate}
                      candidateStatus={candidateJob.status}
                      candidateOrigin={candidateJob.origin}
                      linkTo={`/jobs/${jobId}/candidates/${candidateJob.candidate.id}`}
                      prioritizeWorkExperience
                    />

                    <CandidateCardActions>
                      <div className="flex items-center gap-2">
                        <CandidateScore
                          score={getCandidateDisplayScore(candidateJob)}
                          size={16}
                        />
                        <CandidateAssessmentSummaryBadge
                          assessment={candidateJob.assessment}
                        />
                        {candidateJob.feedbacks?.hmFeedbacks &&
                          candidateJob.feedbacks.hmFeedbacks.length > 0 && (
                            <CandidateCardHMFeedbackBadge
                              hmFeedbacks={candidateJob.feedbacks.hmFeedbacks}
                            />
                          )}
                        {candidateJob.status === 'ContactStarted' &&
                          candidateJob.reachOutMessage && (
                            <CandidateCardReachOutMessage
                              jobId={candidateJob.jobId}
                              reachOutMessage={candidateJob.reachOutMessage}
                            />
                          )}
                      </div>

                      {candidateJob.status === 'Shortlisted' && (
                        <CandidateCardHiringStatus
                          jobId={candidateJob.jobId}
                          candidateId={candidateJob.candidate.id}
                          currentStatus={candidateJob.hiringStatus}
                          hiringStages={hiringStages}
                        />
                      )}
                      <CandidateActionButton
                        candidateJob={candidateJob}
                        job={job}
                      />
                    </CandidateCardActions>
                  </CandidateCardHeader>

                  <Separator />
                  <CandidateCardContent>
                    <CandidateCardBackground
                      workExperiences={candidateJob.candidate.workExperiences}
                      strengths={candidateJob.strengths}
                      clarifications={candidateJob.clarifications}
                    />
                  </CandidateCardContent>
                </CandidateCard>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
